import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Trash2, X } from "lucide-react";
import { apiBoss } from "@/lib/api";
import { VerifiedMobileInput } from "@/components/common/VerifiedInput";

export default function BossCustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", cafe_name: "" });

  const loadCustomers = () => {
    setLoading(true);
    apiBoss.get("/boss/customers")
      .then(({ data }) => setCustomers(data))
      .catch(() => toast.error("Failed to load customers"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const cleanMob = form.mobile.replace(/\D/g, "");
    if (!form.name.trim() || cleanMob.length !== 10) {
      return toast.error("Valid name and 10-digit mobile number are required");
    }
    try {
      await apiBoss.post("/boss/customers", form);
      toast.success("Customer record created");
      setShowAddModal(false);
      setForm({ name: "", mobile: "", cafe_name: "" });
      loadCustomers();
    } catch (err) {
      toast.error("Failed to add customer");
    }
  };

  const handleDelete = async (cid) => {
    if (!window.confirm("Delete customer record?")) return;
    try {
      await apiBoss.delete(`/boss/customers/${cid}`);
      toast.success("Customer deleted");
      loadCustomers();
    } catch (err) {
      toast.error("Failed to delete customer");
    }
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between border-b border-slate-800 pb-6">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">User Base</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-1">Connected Customers Database</h1>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="h-11 px-5 rounded-2xl bg-primary text-white text-xs font-semibold flex items-center gap-2 hover:bg-primary/90 transition-all"
        >
          <Plus className="w-4 h-4" />
          <span>Add Customer</span>
        </button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950 text-slate-400 uppercase font-mono border-b border-slate-800">
              <tr>
                <th className="py-4 px-6">Customer Name</th>
                <th className="py-4 px-6">Mobile Number</th>
                <th className="py-4 px-6">Cafe Connected</th>
                <th className="py-4 px-6">First Connected Date</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-800/70">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500 font-mono">Loading customers...</td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500 font-mono">No customer records in database.</td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-4 px-6 font-bold text-white">{c.name}</td>
                    <td className="py-4 px-6 font-mono text-slate-300">{c.mobile}</td>
                    <td className="py-4 px-6">{c.cafe_name || "General"}</td>
                    <td className="py-4 px-6 font-mono text-[11px] text-slate-400">
                      {c.first_connected_at ? c.first_connected_at.slice(0, 10) : "-"}
                    </td>
                    <td className="py-4 px-6">
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-mono bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        {c.status || "Active"}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <button
                        onClick={() => handleDelete(c.id)}
                        className="p-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-md p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-heading text-lg font-bold text-white">Add New Customer</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1 text-slate-400 hover:bg-slate-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAdd} className="space-y-4 text-xs">
              <div>
                <label className="block font-mono uppercase text-slate-400 mb-1">Customer Name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-white"
                />
              </div>

              <VerifiedMobileInput
                label="Mobile Number * (10-Digit Count)"
                value={form.mobile}
                onChange={(val) => setForm({ ...form, mobile: val })}
              />

              <div>
                <label className="block font-mono uppercase text-slate-400 mb-1">Cafe Visited / Connected</label>
                <input
                  type="text"
                  value={form.cafe_name}
                  onChange={(e) => setForm({ ...form, cafe_name: e.target.value })}
                  className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-white"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="h-10 px-4 rounded-xl border border-slate-800 text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="h-10 px-5 rounded-xl bg-primary text-white font-semibold"
                >
                  Save Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
